import { Outlet, createRootRoute, HeadContent, Scripts } from "@tanstack/react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useState, useEffect, lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "@/lib/auth-context";

import appCss from "../styles.css?url";

// Lazy : ces composants ne sont nécessaires qu'après authentification
// (et pas du tout sur la SPA, qui utilise son propre main.tsx).
const AppShell = lazy(() =>
  import("@/components/AppShell").then((m) => ({ default: m.AppShell })),
);
const TooltipProvider = lazy(() =>
  import("@/components/ui/tooltip").then((m) => ({ default: m.TooltipProvider })),
);
const Toaster = lazy(() =>
  import("@/components/ui/sonner").then((m) => ({ default: m.Toaster })),
);

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-display font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page introuvable</h2>
        <p className="mt-2 text-sm text-muted-foreground">Cette page n'existe pas ou a été déplacée.</p>
        <a href="/" className="mt-6 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">Retour à l'accueil</a>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Gest'Asso 1.0 — Gestion d'association" },
      { name: "description", content: "Application de gestion pour le bureau de votre association : adhérents, trésorerie, mailing." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  if (typeof document !== "undefined") {
    return <>{children}</>;
  }

  return (
    <html lang="fr">
      <head><HeadContent /></head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const PUBLIC_PATHS = ["/login", "/reset-password"];
  const isPublic = (p: string) => PUBLIC_PATHS.includes(p);

  useEffect(() => {
    const path = typeof window !== "undefined" ? window.location.pathname : "/";
    if (mounted && !loading && !user && !isPublic(path)) {
      window.location.href = "/login";
    }
  }, [mounted, loading, user]);

  // Pendant SSR et avant montage client : rendre un placeholder neutre identique
  if (!mounted || loading) {
    return (
      <div suppressHydrationWarning className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-sm text-muted-foreground">Chargement…</div>
      </div>
    );
  }
  const path = typeof window !== "undefined" ? window.location.pathname : "/";
  if (isPublic(path)) return <>{children}</>;
  if (!user) return null;
  return (
    <Suspense fallback={null}>
      <AppShell>{children}</AppShell>
    </Suspense>
  );
}

function RootComponent() {
  const [queryClient] = useState(() => new QueryClient({ defaultOptions: { queries: { staleTime: 30_000 } } }));
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <Suspense fallback={null}>
          <TooltipProvider>
            <AuthGate>
              <Outlet />
            </AuthGate>
          </TooltipProvider>
        </Suspense>
        <Suspense fallback={null}>
          <Toaster richColors position="top-right" />
        </Suspense>
      </AuthProvider>
    </QueryClientProvider>
  );
}
